<script setup lang="ts">
import {computed, ref} from 'vue'
import {IScreenShareSource} from 'toolify-desktop/src/main/types/IScreenShareSource'
import ModalComponent from '@toolify/client/src/modules/modals/components/ModalComponent.vue'
import ButtonComponent from '@toolify/client/src/modules/ui/components/ButtonComponent.vue'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import ScrollbarComponent from '@toolify/client/src/modules/ui/components/ScrollbarComponent.vue'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'

interface Props {
  sources: IScreenShareSource[]
}
const props = defineProps<Props>()

interface Emits {
  (e: 'select', sourceId: string): void
  (e: 'cancel'): void
}
const emit = defineEmits<Emits>()

const {getThemeVariable} = useThemeVariables()
const selectedSourceId = ref<string | null>(props.sources[0]?.id || null)

const screenSources = computed(() => {
  return props.sources.filter(source => source.isScreen)
})

const windowSources = computed(() => {
  return props.sources.filter(source => !source.isScreen)
})

function onShare() {
  if (!selectedSourceId.value) {
    return
  }
  emit('select', selectedSourceId.value)
}
</script>

<template>
  <ModalComponent :openOnMount="true" :maxWidth="760" @exit="emit('cancel')">
    <template #content>
      <div class="screen-share-picker">
        <div class="picker-title">{{$t('call.chooseScreenShareSource')}}</div>
        <ScrollbarComponent class="picker-scroll" :gradientColor="getThemeVariable(ThemeVariableType.SurfaceColor)">
          <div class="picker-section" v-if="screenSources.length">
            <div class="section-label">{{$t('call.screenShareScreens')}}</div>
            <div class="source-grid">
              <button
                type="button"
                class="source-tile"
                v-for="source in screenSources"
                :key="source.id"
                :class="{'is-selected': source.id === selectedSourceId}"
                @click="selectedSourceId = source.id"
                @dblclick="emit('select', source.id)"
              >
                <img
                  class="source-thumbnail"
                  v-if="source.thumbnailDataUrl"
                  :src="source.thumbnailDataUrl"
                  alt=""
                />
                <div class="source-thumbnail is-empty" v-else>
                  <IconComponent :name="IconNameType.Display" :weightType="IconWeightType.Solid"/>
                </div>
                <div class="source-name">{{source.name}}</div>
              </button>
            </div>
          </div>
          <div class="picker-section" v-if="windowSources.length">
            <div class="section-label">{{$t('call.screenShareWindows')}}</div>
            <div class="source-grid">
              <button
                type="button"
                class="source-tile"
                v-for="source in windowSources"
                :key="source.id"
                :class="{'is-selected': source.id === selectedSourceId}"
                @click="selectedSourceId = source.id"
                @dblclick="emit('select', source.id)"
              >
                <img
                  class="source-thumbnail"
                  v-if="source.thumbnailDataUrl"
                  :src="source.thumbnailDataUrl"
                  alt=""
                />
                <div class="source-thumbnail is-empty" v-else>
                  <IconComponent :name="IconNameType.WindowRestore" :weightType="IconWeightType.Solid"/>
                </div>
                <div class="source-name">
                  <img
                    class="source-icon"
                    v-if="source.appIconDataUrl"
                    :src="source.appIconDataUrl"
                    alt=""
                  />
                  <span>{{source.name}}</span>
                </div>
              </button>
            </div>
          </div>
        </ScrollbarComponent>
        <div class="picker-actions">
          <ButtonComponent
            :color="ColorVariantType.Neutral"
            :style="StyleVariantType.ClearFilled"
            @click="emit('cancel')"
          >
            {{$t('general.cancel')}}
          </ButtonComponent>
          <ButtonComponent
            :color="ColorVariantType.Primary"
            :isDisabled="!selectedSourceId"
            @click="onShare"
          >
            {{$t('call.shareScreen')}}
          </ButtonComponent>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;
@use '@toolify/client/src/styles/light' as light;

.screen-share-picker {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: map-get(variables.$spacing, space-3);
  width: 100%;
  max-height: 70vh;
}

.picker-title {
  font-size: map-get(variables.$textSizes, text-lg);
  font-weight: 600;
  color: map-get(variables.$colors, content-light);
}

.picker-scroll {
  min-height: 0;
}

.picker-section {
  display: grid;
  gap: map-get(variables.$spacing, space-2);
  margin-bottom: 16px;
}

.section-label {
  font-size: map-get(variables.$textSizes, text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: map-get(variables.$colors, content-dim);
}

.source-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: map-get(variables.$spacing, space-2);
}

.source-tile {
  display: grid;
  gap: map-get(variables.$spacing, space-1-5);
  padding: map-get(variables.$spacing, space-1-5);
  border: 0.125rem solid transparent;
  border-radius: map-get(variables.$componentRadius, standard);
  background: map-get(variables.$colors, container);
  cursor: pointer;
  font: inherit;
  color: map-get(variables.$colors, content-normal);
  text-align: left;

  // light: kafel zrodla jako biala karta z obrysem atramentu (byl szarym blokiem), hover - nakladka atramentu
  @include light.only {
    background: map-get(variables.$colors, raised-surface);
    box-shadow: 0 0 0 0.0625rem map-get(variables.$colors, raised-border);
  }

  &:hover {
    background: map-get(variables.$colors, container-high);

    @include light.only {
      background: map-get(variables.$colors, raised-hover);
    }
  }

  &.is-selected {
    border-color: map-get(variables.$colors, primary);
  }
}

.source-thumbnail {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: contain;
  border-radius: map-get(variables.$componentRadius, standard);
  background: map-get(variables.$colors, surface);

  @include light.only {
    background: map-get(variables.$colors, raised-panel);
  }

  &.is-empty {
    display: grid;
    place-items: center;
    color: map-get(variables.$colors, content-dim);
  }
}

.source-name {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-1-5);
  min-width: 0;
  font-size: map-get(variables.$textSizes, text-sm);

  span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.source-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.picker-actions {
  display: flex;
  justify-content: flex-end;
  gap: map-get(variables.$spacing, space-2);
}
</style>
